import React, { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/** Provided by the case page: the DOM slot beside the scrolling document, whether there is room to dock, and the size model. */
export const InspectorSlot = createContext({ el: null, dock: false, mobile: false, width: 460, dockW: 0, wide: false, setWide: null, setOpen: null });

const iconBtn = "flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent";

function Head({ eyebrow, title, sub, status, actions, onPrev, onNext, onClose, testId, wide, setWide, dock, noWide }) {
  return (
    <div className="sticky top-0 z-10 border-b border-black/[0.06] bg-white px-6 pb-4 pt-5" data-testid={`${testId}-head`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {eyebrow ? <div className="text-[13px] text-muted-foreground">{eyebrow}</div> : null}
          <div className="mt-0.5 text-[19px] font-semibold leading-[1.25] tracking-[-0.015em] text-foreground">{title}</div>
          {sub ? <div className="mt-1 truncate text-[13.5px] text-muted-foreground">{sub}</div> : null}
        </div>
        <div className="-mr-2 -mt-1.5 flex shrink-0 items-center">
          {onPrev || onNext ? (
            <>
              <button type="button" onClick={onPrev} disabled={!onPrev} className={cn(iconBtn, "hidden sm:flex")} aria-label="Forrige" data-testid={`${testId}-prev`}><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" onClick={onNext} disabled={!onNext} className={cn(iconBtn, "hidden sm:flex")} aria-label="Neste" data-testid={`${testId}-next`}><ChevronRight className="h-4 w-4" /></button>
            </>
          ) : null}
          {actions}
          {dock && setWide && !noWide ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => setWide(!wide)} aria-pressed={wide} className={cn(iconBtn, "hidden lg:flex")} aria-label={wide ? "Smalere panel" : "Utvid panelet"} data-testid={`${testId}-wide`}>{wide ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}</button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="rounded-lg px-2.5 py-1 text-[12px]">{wide ? "Tilbake til vanlig bredde" : "Utvid panelet"}</TooltipContent>
            </Tooltip>
          ) : null}
          <button type="button" onClick={onClose} className={iconBtn} aria-label="Lukk" data-testid={`${testId}-close`}><X className="h-4 w-4" /></button>
        </div>
      </div>
      {status ? <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">{status}</div> : null}
    </div>
  );
}

const escClose = (onClose) => (e) => {
  const t = e.target;
  if (e.key === "Escape" && !e.defaultPrevented && !(t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable))) { e.preventDefault(); onClose(); }
};

/**
 * One post, one place. Docked as its own full-height column beside the document when the surface is wide enough
 * (the list never reflows); otherwise it slides in over the right edge, from the bottom on phones. One scrollbar, sticky head.
 * The dock is resizable from its left edge and can be widened to most of the surface for long posts.
 */
export function Inspector({ onClose, eyebrow, title, sub, status, actions, onPrev, onNext, children, testId = "inspector", label, noWide = false, footer = null }) {
  const { el, dock, mobile, width, wide, setWide, setOpen } = useContext(InspectorSlot);
  useEffect(() => { setOpen?.(true); return () => setOpen?.(false); }, [setOpen]);
  const head = <Head eyebrow={eyebrow} title={title} sub={sub} status={status} actions={actions} onPrev={onPrev} onNext={onNext} onClose={onClose} testId={testId} wide={wide} setWide={setWide} dock={dock} noWide={noWide} />;
  const body = <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-12 pt-5 scrollbar-thin">{children}</div>;
  if (dock && el) {
    return createPortal(
      <aside onKeyDown={escClose(onClose)} className="flex h-full w-full flex-col bg-white" aria-label={label || "Detaljer"} data-testid={testId} data-placement="dock" data-wide={wide ? "1" : "0"}>{head}{body}{footer}</aside>,
      el,
    );
  }
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side={mobile ? "bottom" : "right"} hideClose aria-describedby={undefined} className={cn("flex flex-col gap-0 p-0", mobile ? "h-[92dvh] rounded-t-[20px]" : "w-[min(92vw,var(--insp-w))] sm:max-w-[min(92vw,var(--insp-w))]")} style={{ "--insp-w": `${width}px` }} data-testid={testId} data-placement={mobile ? "sheet-bottom" : "sheet-right"}>
        <SheetTitle className="sr-only">{label || "Detaljer"}</SheetTitle>
        {head}
        {body}
        {footer}
      </SheetContent>
    </Sheet>
  );
}
